import React, { useState } from "react";
import { Box, Collapse, Typography, useTheme } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useTranslation } from "react-i18next";
import LogMessage from "../LogMessage";
import ActivityLogDetail from "./ActivityLogDetail";
import {
  activityHasDetail,
  activityTimeTitle,
  formatActivityClock,
  getActivityActor,
  getActivityChanges,
  getActivityPresentation,
  getTaskLogRef,
  isRequestStatusLabel,
} from "../activityLogDisplay";
import { useActivityTaskOpener } from "../ActivityTaskModal";
import VideoCallSummaryMessage, { resolveVideoCallSummary } from "../VideoCallSummaryMessage";
import VoiceCallLogMessage, { resolveVoiceCallLog } from "../VoiceCallLogMessage";
import PartnerApiAuditPanel from "../PartnerApiAuditPanel";
import AiAgentAuditPanel from "../AiAgentAuditPanel";
import SurveySessionDetailDialog from "../../SurveySessionDetailDialog";
import {
  getMessageDate,
  setActivityExpandPreference,
  getLogActorDisplayName,
  getActivityLogSummary,
} from "../timelineUtils";
import { isLeadAccessLog, isMasterTechnicalLogMessage } from "../messageTypeRegistry";

const ACCESS_FIELD_KEYS = {
  nome: "timeline.accessFieldNome",
  email: "timeline.accessFieldEmail",
  telefone: "timeline.accessFieldTelefone",
  celular: "timeline.accessFieldCelular",
  whatsapp: "timeline.accessFieldWhatsapp",
  cpf: "timeline.accessFieldCpf",
  cnpj: "timeline.accessFieldCnpj",
  identidade: "timeline.accessFieldIdentidade",
  data_nascimento: "timeline.accessFieldDataNascimento",
  endereco: "timeline.accessFieldEndereco",
  cidade: "timeline.accessFieldCidade",
  cep: "timeline.accessFieldCep",
};

const formatTime = (message) => formatActivityClock(getMessageDate(message));

const ActorIcon = ({ kind }) => {
  const sx = { fontSize: 14, flexShrink: 0 };
  if (kind === "chatbot") return <SmartToyOutlinedIcon sx={sx} />;
  if (kind === "ai_agent") return <AutoAwesomeOutlinedIcon sx={sx} />;
  if (kind === "automation") return <BoltOutlinedIcon sx={sx} />;
  if (kind === "user") return <PersonOutlineIcon sx={sx} />;
  return <SettingsOutlinedIcon sx={sx} />;
};

const InlineStatus = ({ label }) => {
  if (!isRequestStatusLabel(label)) {
    return (
      <Box component="span" sx={{ fontWeight: 600 }}>
        {label}
      </Box>
    );
  }
  return (
    <Box
      component="span"
      sx={{
        px: 0.7,
        py: 0.05,
        borderRadius: 999,
        bgcolor: "action.selected",
        fontWeight: 700,
        fontSize: "0.92em",
      }}
    >
      {label}
    </Box>
  );
};

const formatWhen = (raw) => {
  if (!raw) return "";
  const d = raw instanceof Date ? raw : new Date(String(raw).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const LeadAccessDetails = ({ message, t }) => {
  const meta = message?.logMeta || {};
  const conv = Number(meta.conversationCount || 0);
  const notes = Number(meta.noteCount || 0);
  const fields = Array.isArray(meta.clientFields) ? meta.clientFields : [];
  const fieldLabels = fields.map((key) => t(ACCESS_FIELD_KEYS[key] || key, key));
  const origem = meta.origem === "popup" ? t("timeline.accessOriginPopup", "Acesso pela janela de chamada") : t("timeline.accessOriginChat", "Acesso pelo chat");
  const lastWhen = formatWhen(meta.lastMessageAt);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}>
      {conv > 0 ? (
        <Typography variant="caption" component="div">
          {t("timeline.accessHistoryLine", "{{count}} mensagem(ns) no histórico com o cliente", { count: conv })}
        </Typography>
      ) : null}
      {notes > 0 ? (
        <Typography variant="caption" component="div">
          {t("timeline.accessNotesLine", "{{count}} anotação(ões) interna(s)", { count: notes })}
        </Typography>
      ) : null}
      {fieldLabels.length ? (
        <Typography variant="caption" component="div">
          {t("timeline.accessClientFields", "Cadastro visível: {{fields}}", {
            fields: fieldLabels.join(", "),
          })}
        </Typography>
      ) : null}
      {lastWhen ? (
        <Typography variant="caption" component="div">
          {t("timeline.accessLastMessage", "Última mensagem do histórico em {{when}}", { when: lastWhen })}
        </Typography>
      ) : null}
      <Typography variant="caption" component="div">
        {origem}
      </Typography>
    </Box>
  );
};

const getLogSummary = (message, loggedUser) => getActivityLogSummary(message, loggedUser);

const ActivityLogMessage = ({ message, defaultExpanded = false, loggedUser, leadId = null }) => {
  const theme = useTheme();
  const { t } = useTranslation("chat");
  const videoSummary = resolveVideoCallSummary(message);
  const voiceCall = resolveVoiceCallLog(message);
  const isTechnical = isMasterTechnicalLogMessage(message);
  const isAccess = isLeadAccessLog(message);
  const canExpand =
    isAccess ||
    activityHasDetail(message) ||
    Boolean(resolveVoiceCallLog(message)) ||
    isTechnical;
  const [expanded, setExpanded] = useState(Boolean(defaultExpanded) && canExpand);
  const [surveyDetailOpen, setSurveyDetailOpen] = useState(false);
  const [taskHint, setTaskHint] = useState("");
  const openActivityTask = useActivityTaskOpener();
  const taskRef = isLeadAccessLog(message) ? null : getTaskLogRef(message);
  const presentation = isAccess ? null : getActivityPresentation(message);
  const summary = isAccess
    ? t("timeline.accessViewed", "Visualizou esta solicitação")
    : presentation.headline || getLogSummary(message, loggedUser);
  const motivo = presentation?.motivo || "";
  const origin = presentation?.origin || "";
  const extra = presentation?.extra || "";
  const time = formatTime(message);
  const actor = getActivityActor(message, loggedUser);
  const actorName = isAccess ? getLogActorDisplayName(message, loggedUser) : actor.label;
  const changes = getActivityChanges(message);
  const singleStatus =
    !isAccess &&
    changes.length === 1 &&
    (changes[0].field === "status" || changes[0].field === "status_change" || isRequestStatusLabel(changes[0].toLabel));
  const hasDetail = !isAccess && activityHasDetail(message);
  const whenTitle = activityTimeTitle(getMessageDate(message));
  const surveyLogMeta = message?.logMeta?.code === "survey_response" ? message.logMeta : null;

  // Resumo de vídeo: um card só (sem accordion + texto duplicado).
  if (videoSummary) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          py: 0.75,
          px: 1,
        }}
      >
        <VideoCallSummaryMessage message={message} />
      </Box>
    );
  }

  const toggle = () => {
    if (!canExpand) return;
    const next = !expanded;
    setExpanded(next);
    if (!isAccess) setActivityExpandPreference(next);
  };

  const openSurveyDetail = (event) => {
    event?.stopPropagation?.();
    setSurveyDetailOpen(true);
  };

  const openTask = async (event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!taskRef) return;
    setTaskHint("");
    if (!openActivityTask) {
      setTaskHint("Tarefa não encontrada");
      return;
    }
    try {
      await openActivityTask({ cardId: taskRef.cardId, title: taskRef.title, leadId });
    } catch {
      setTaskHint("Tarefa não encontrada");
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        py: 0.25,
      }}
    >
      <Box sx={{ maxWidth: "min(440px, 92%)", width: "100%", textAlign: "center" }}>
        <Box
          component="button"
          type="button"
          onClick={taskRef ? openTask : toggle}
          title={whenTitle}
          sx={{
            display: "inline-flex",
            alignItems: "flex-start",
            gap: 0.6,
            border: "none",
            bgcolor: "transparent",
            cursor: taskRef || canExpand ? "pointer" : "default",
            color: theme.palette.text.secondary,
            fontSize: "0.75rem",
            py: 0.35,
            px: 0.75,
            borderRadius: 1,
            maxWidth: "100%",
            textAlign: "center",
            justifyContent: "center",
            "&:hover": { bgcolor: theme.palette.action.hover },
          }}
        >
          <Box sx={{ mt: "1px", color: theme.palette.text.disabled }}>
            {isAccess ? (
              <VisibilityOutlinedIcon
                sx={{ fontSize: 14, color: theme.palette.info.main, flexShrink: 0 }}
              />
            ) : (
              <ActorIcon kind={actor.kind} />
            )}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              component="div"
              variant="caption"
              sx={{
                fontSize: "inherit",
                color: theme.palette.text.secondary,
                fontWeight: 400,
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.35,
              }}
            >
              {taskRef ? (
                <Box
                  component="span"
                  sx={{
                    cursor: "pointer",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {summary}
                </Box>
              ) : (
                summary
              )}
              {singleStatus ? (
                <>
                  {" "}
                  <InlineStatus label={changes[0].fromLabel} />
                  {" → "}
                  <InlineStatus label={changes[0].toLabel} />
                </>
              ) : null}
            </Typography>
            {taskHint ? (
              <Typography component="div" variant="caption" sx={{ display: "block", mt: 0.15, color: theme.palette.text.disabled }}>
                {taskHint}
              </Typography>
            ) : null}
            {origin ? (
              <Typography
                component="div"
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.1,
                  color: theme.palette.text.disabled,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.35,
                }}
              >
                {origin}
              </Typography>
            ) : null}
            {motivo ? (
              <Typography
                component="div"
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.15,
                  color: theme.palette.text.secondary,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.35,
                }}
              >
                {motivo}
              </Typography>
            ) : null}
            <Typography
              component="div"
              variant="caption"
              sx={{
                display: "block",
                mt: 0.15,
                color: theme.palette.text.disabled,
                whiteSpace: "normal",
                lineHeight: 1.3,
              }}
            >
              {actorName || ""}
              {actor.deleted ? (
                <Box
                  component="span"
                  title="Esta conta foi excluída"
                  sx={{
                    ml: 0.6,
                    px: 0.6,
                    py: 0.05,
                    borderRadius: 10,
                    border: "1px solid",
                    borderColor: "divider",
                    fontSize: "0.65rem",
                    lineHeight: 1.4,
                    verticalAlign: "1px",
                  }}
                >
                  excluído
                </Box>
              ) : null}
              {surveyLogMeta ? (
                <>
                  {" · "}
                  <Box
                    component="span"
                    role="link"
                    tabIndex={0}
                    onClick={openSurveyDetail}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openSurveyDetail(event);
                      }
                    }}
                    sx={{
                      color: theme.palette.primary.main,
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    Ver respostas
                  </Box>
                </>
              ) : null}
              {time ? ` · ${time}` : ""}
            </Typography>
          </Box>
          {canExpand ? (
            expanded ? (
              <ExpandLessIcon sx={{ fontSize: 14, flexShrink: 0, mt: "1px" }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 14, flexShrink: 0, mt: "1px" }} />
            )
          ) : null}
        </Box>

        {/* Detalhes técnicos sempre acessíveis (fora do collapse do texto). */}
        <PartnerApiAuditPanel message={message} compact />
        <AiAgentAuditPanel message={message} compact />

        <Collapse in={expanded}>
          <Box
            sx={{
              mt: 0.25,
              px: 1,
              py: 0.5,
              textAlign: "center",
              fontSize: "0.75rem",
              color: theme.palette.text.secondary,
              "& .MuiTypography-root": { fontSize: "0.75rem !important" },
            }}
          >
            {isAccess ? (
              <LeadAccessDetails message={message} t={t} />
            ) : voiceCall ? (
              <VoiceCallLogMessage message={message} />
            ) : hasDetail ? (
              <>
                {extra && !taskRef ? (
                  <Typography variant="caption" component="div" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", mb: 0.5 }}>
                    {extra}
                  </Typography>
                ) : null}
                <ActivityLogDetail message={message} />
              </>
            ) : (
              <LogMessage message={message} loggedUser={loggedUser} />
            )}
            {surveyLogMeta ? (
              <Typography
                component="button"
                type="button"
                variant="caption"
                onClick={openSurveyDetail}
                sx={{
                  display: "inline-block",
                  mt: 0.5,
                  border: "none",
                  bgcolor: "transparent",
                  p: 0,
                  color: theme.palette.primary.main,
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                Ver respostas da pesquisa
              </Typography>
            ) : null}
          </Box>
        </Collapse>
      </Box>

      {surveyLogMeta ? (
        <SurveySessionDetailDialog
          open={surveyDetailOpen}
          onClose={() => setSurveyDetailOpen(false)}
          surveyId={surveyLogMeta.surveyId}
          sessionId={surveyLogMeta.sessionId}
          leadId={surveyLogMeta.leadId}
          surveyTitle={surveyLogMeta.surveyTitle}
        />
      ) : null}
    </Box>
  );
};

export default ActivityLogMessage;
